// src/features/streams/components/StreamOverviewTab.tsx

import { Card, Group, Text, Button, Stack, Box } from '@mantine/core';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { notifications } from '@mantine/notifications';
import { StreamOverviewSchema, type StreamOverviewFormData } from '../schemas/StreamForm.schema';
import { useUpdateStreamMedia, useUpdateStreamSettings } from '../useStreamMutations';
import { StreamUtils } from '../StreamUtils';
import { StreamGeneralInfoForm } from './StreamGeneralInfoForm';
import { StreamControlsSection } from './StreamControlsSection';
import { useTags } from '@/features/tag/useTags';
import type { StreamViewData } from '@/lib/types/Stream';

interface StreamOverviewTabProps {
  stream: StreamViewData;
}

export function StreamOverviewTab({ stream }: StreamOverviewTabProps) {
  const mediaId = stream.media.mediaId;
  const updateMediaMutation = useUpdateStreamMedia(mediaId);
  const updateStreamMutation = useUpdateStreamSettings(mediaId);
  const { tags: allTags } = useTags();

  const { control, watch, handleSubmit, formState: { isDirty, errors }, reset } = useForm<StreamOverviewFormData>({
    resolver: zodResolver(StreamOverviewSchema),
    defaultValues: StreamUtils.transformToOverviewFormData(stream),
  });

  const onSubmit = async (data: StreamOverviewFormData) => {
    const originalRefId = stream.media.refId ?? '';
    const refIdChanged = data.refId !== originalRefId;

    try {
      // 1. Save media fields first
      const mediaReq = StreamUtils.transformOverviewToMediaUpdateRequest(data, refIdChanged);
      await updateMediaMutation.mutateAsync(mediaReq);

      // 2. Save stream settings (only if media succeeded)
      const streamReq = StreamUtils.transformOverviewToStreamUpdateRequest(data, stream);
      await updateStreamMutation.mutateAsync(streamReq);

      // 3. Both succeeded -- reset form
      reset(data);
      notifications.show({
        title: 'Saved',
        message: `Updated "${data.title}"`,
        color: 'green',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save changes';
      notifications.show({
        title: 'Error',
        message,
        color: 'red',
      });
    }
  };

  const onInvalid = (fieldErrors: typeof errors) => {
    const firstError = Object.values(fieldErrors).find(e => e?.message);
    if (firstError?.message) {
      notifications.show({
        title: 'Validation Error',
        message: firstError.message,
        color: 'orange',
      });
    }
  };

  const isPending = updateMediaMutation.isPending || updateStreamMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <Stack gap="xl" maw={800}>
        {/* Sticky action bar */}
        <Box
          pos="sticky"
          top="calc(var(--app-shell-header-height, 60px) + var(--mantine-spacing-md))"
          bg="white"
          py="sm"
          style={{ zIndex: 10, borderBottom: '1px solid var(--mantine-color-gray-2)' }}
        >
          <Group justify="space-between">
            <Text fw={600} size="lg">Stream Settings</Text>
            <Group gap="sm">
              {isDirty && (
                <Button variant="default" onClick={() => reset()} disabled={isPending}>
                  Discard
                </Button>
              )}
              <Button type="submit" disabled={!isDirty} loading={isPending}>
                Save Changes
              </Button>
            </Group>
          </Group>
        </Box>

        {/* Stream Controls Card */}
        <Card shadow="sm" padding="lg" withBorder>
          <Text fw={600} mb="md">Stream Controls</Text>
          <StreamControlsSection control={control} watch={watch} stream={stream} />
        </Card>

        {/* General Information Card */}
        <Card shadow="sm" padding="lg" withBorder>
          <Text fw={600} mb="md">General Information</Text>
          <StreamGeneralInfoForm control={control} watch={watch} allTags={allTags} />
        </Card>
      </Stack>
    </form>
  );
}
